import React, { useState, useEffect } from 'react';
import { 
  Alert, 
  AlertTitle, 
  Box, 
  Chip, 
  IconButton, 
  Collapse,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  WifiOff as WifiOffIcon,
  Wifi as WifiIcon,
  Close as CloseIcon,
  CloudOff as CloudOffIcon,
  CloudDone as CloudDoneIcon
} from '@mui/icons-material';

interface OfflineIndicatorProps {
  /** Override the automatic network detection */
  isOnline?: boolean;
  /** Show detailed offline information */
  showDetails?: boolean;
  /** Callback when user dismisses the indicator */
  onDismiss?: () => void;
  /** Show as a floating indicator instead of inline */
  floating?: boolean;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  isOnline: overrideOnline,
  showDetails = false,
  onDismiss,
  floating = false
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isVisible, setIsVisible] = useState(!navigator.onLine);
  const [pendingActions, setPendingActions] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (overrideOnline !== undefined) {
      setIsOnline(overrideOnline);
      setIsVisible(!overrideOnline);
      return;
    }

    const handleOnline = () => {
      setIsOnline(true);
      setIsVisible(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsVisible(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [overrideOnline]);

  useEffect(() => {
    // Check for pending actions in IndexedDB (placeholder for actual implementation)
    const checkPendingActions = async () => {
      try {
        // This would connect to IndexedDB to check for queued offline actions
        // For now, we'll simulate this
        const stored = localStorage.getItem('lighthouse-pending-actions');
        const count = stored ? JSON.parse(stored).length : 0;
        setPendingActions(count);
      } catch (error) {
        console.error('Failed to check pending actions:', error);
      }
    };

    checkPendingActions();
    const interval = setInterval(checkPendingActions, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) {
    return null;
  }

  const StatusIcon = isOnline ? CloudDoneIcon : CloudOffIcon;
  const NetworkIcon = isOnline ? WifiIcon : WifiOffIcon;

  const baseStyles = {
    ...(floating && {
      position: 'fixed' as const,
      top: isMobile ? '16px' : '24px',
      right: isMobile ? '16px' : '24px',
      zIndex: theme.zIndex.snackbar,
      maxWidth: isMobile ? 'calc(100vw - 32px)' : '400px',
      boxShadow: theme.shadows[8],
    }),
    borderRadius: isMobile ? '8px' : '12px',
  };

  if (!showDetails) {
    // Simple chip indicator
    return (
      <Box sx={floating ? baseStyles : {}}>
        <Chip
          icon={<NetworkIcon fontSize="small" />}
          label={isOnline ? 'Online' : 'Offline'}
          color={isOnline ? 'success' : 'warning'}
          variant={isOnline ? 'outlined' : 'filled'}
          size={isMobile ? 'medium' : 'small'}
          onDelete={onDismiss ? handleDismiss : undefined}
          deleteIcon={onDismiss ? <CloseIcon fontSize="small" /> : undefined}
          sx={{
            fontWeight: 500,
            ...(floating && {
              backdropFilter: 'blur(8px)',
              backgroundColor: isOnline 
                ? 'rgba(76, 175, 80, 0.9)' 
                : 'rgba(255, 152, 0, 0.9)',
            }),
          }}
        />
      </Box>
    );
  }

  // Detailed alert indicator
  return (
    <Box sx={floating ? baseStyles : {}}>
      <Collapse in={isVisible}>
        <Alert
          severity={isOnline ? 'success' : 'warning'}
          icon={<StatusIcon />}
          action={
            onDismiss && (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleDismiss}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            )
          }
          sx={{
            '& .MuiAlert-message': {
              width: '100%',
            },
            ...(floating && {
              backdropFilter: 'blur(8px)',
              backgroundColor: isOnline 
                ? 'rgba(237, 247, 237, 0.95)' 
                : 'rgba(255, 243, 224, 0.95)',
            }),
          }}
        >
          <AlertTitle sx={{ marginBottom: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <NetworkIcon fontSize="small" />
              {isOnline ? 'Back Online' : 'Working Offline'}
            </Box>
          </AlertTitle>
          
          <Typography variant="body2" sx={{ marginBottom: pendingActions > 0 ? 1 : 0 }}>
            {isOnline 
              ? 'Your connection has been restored. Syncing any pending changes...'
              : 'You\'re currently offline. Changes will be saved locally and synced when connection is restored.'
            }
          </Typography>

          {pendingActions > 0 && (
            <Box sx={{ marginTop: 1 }}>
              <Chip
                size="small"
                label={`${pendingActions} pending action${pendingActions > 1 ? 's' : ''}`}
                color={isOnline ? 'info' : 'default'}
                icon={<CloudOffIcon fontSize="small" />}
                sx={{ fontSize: '0.75rem' }}
              />
            </Box>
          )}
        </Alert>
      </Collapse>
    </Box>
  );
};

export default OfflineIndicator;